import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Badge,
  Button,
  CircularProgress,
  useTheme,
  alpha,
  Stack,
  Tooltip,
  useMediaQuery,
  Fade,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsIcon from "@mui/icons-material/Settings";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNotificationsV2 } from "../../hooks/useNotificationsV2";
import VirtualList from "../VirtualList/VirtualList";
import { minifyAddress } from "../../support/utils";
import { formatNumberHumanize } from "../../utils/number";
import { NOTIFICATION_STATUS } from "../../constants/notifications";
import sound from "../../config/sound.mp3";
import useSound from "use-sound";
const TransactionItem = ({ notification, readStatus, onRead, onExpand }) => {
  const theme = useTheme();
  const { transaction } = notification;

  const handleExpand = () => {
    onExpand(notification.id);
    if (readStatus === NOTIFICATION_STATUS.UNREAD) {
      onRead(notification.id);
    }
  };

  if (!transaction) {
    console.log("transaction is null", notification);
    return null;
  }

  return (
    <Paper
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        height: "100%",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        },
      }}
      elevation={2}
    >
      {/* Unread Indicator */}
      {readStatus === NOTIFICATION_STATUS.UNREAD && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: theme.palette.primary.main,
            zIndex: 1,
          }}
        />
      )}

      <Box
        sx={{
          p: 2,
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          position: "relative",
          zIndex: 2,
          height: "100%",
        }}
        onClick={handleExpand}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: "100%" }}
        >
          <Typography variant="body2" color="text.secondary">
            {new Date(transaction.createdAt).toLocaleString()} {notification.id}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {transaction.network?.explorer && (
            <Tooltip title="View on Explorer">
              <IconButton
                size="small"
                href={`${transaction.network.explorer}/tx/${transaction.hash}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {/* Transaction Details */}
        {transaction.from && transaction.to && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {minifyAddress(transaction.from)}
            </Typography>
            <ArrowRightAltIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
              {minifyAddress(transaction.to)}
            </Typography>
          </Stack>
        )}

        {/* Method */}
        {transaction.method && (
          <Typography variant="body2" color="text.secondary">
            Method: {transaction.method}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const TrackedTransactions = () => {
  const theme = useTheme();
  const [play] = useSound(sound);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const listRef = useRef(null);

  // Calculate item height based on screen size
  const itemHeight = isMobile ? 140 : 120;

  const {
    notifications,
    readStatus,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    setNotifications,
  } = useNotificationsV2();

  // Track scroll position for scroll-to-top button
  const handleScroll = useCallback((e) => {
    setShowScrollTop(e.target.scrollTop > 100);
  }, []);

  const scrollToTop = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  const handleExpand = useCallback((notificationId) => {
    setExpandedNotifications((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  }, []);

  const renderItem = useCallback(
    (notification) => (
      <Box sx={{ pb: 1, height: "100%" }}>
        <TransactionItem
          notification={notification}
          readStatus={readStatus[notification.id]}
          onRead={markAsRead}
          onExpand={handleExpand}
        />
      </Box>
    ),
    [readStatus, markAsRead, handleExpand]
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        mx: { xs: 0, sm: 4, md: 6, lg: 20 },
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Badge
            badgeContent={
              unreadCount > 1000
                ? formatNumberHumanize(unreadCount, { decimals: 1 }) + "+"
                : unreadCount
            }
            color="primary"
          >
            <NotificationsNoneIcon />
          </Badge>
          <Typography variant="h6" component="h1">
            Tracked Transactions
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<DoneAllIcon />}
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            size="small"
          >
            Mark all as read
          </Button>
          <Button
            size="small"
            onClick={() => {
              const testNotification = {
                id: Date.now(),
                status: NOTIFICATION_STATUS.UNREAD,
                transaction: {
                  id: Date.now(),
                  hash: "0x" + Math.random().toString(16).substring(2),
                  from: "0x" + Math.random().toString(16).substring(2),
                  to: "0x" + Math.random().toString(16).substring(2),
                  method: "test",
                  createdAt: new Date().toISOString(),
                  network: {
                    explorer: "https://etherscan.io",
                  },
                },
              };
              setNotifications((prev) => [testNotification, ...prev]);
              // setReadStatus((prev) => ({
              //   ...prev,
              //   [testNotification.id]: NOTIFICATION_STATUS.UNREAD,
              // }));
              // setUnreadCount((prev) => prev + 1);
            }}
          >
            Add Test Data
          </Button>
          <IconButton size="small">
            <SettingsIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Transactions List */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading && !notifications.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography color="error">
              Error loading transactions: {error.message}
            </Typography>
          </Paper>
        ) : notifications.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }} elevation={2}>
            <NotificationsNoneIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No transactions yet
            </Typography>
            <Typography color="text.secondary">
              New transactions will appear here
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              width: "100%",
            }}
          >
            {/* Scroll to Top Button */}
            <Fade in={showScrollTop}>
              <IconButton
                onClick={scrollToTop}
                sx={{
                  position: "absolute",
                  right: 16,
                  bottom: 16,
                  zIndex: 10,
                  bgcolor: theme.palette.background.paper,
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: theme.palette.background.paper,
                  },
                }}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </Fade>

            <VirtualList
              ref={listRef}
              items={notifications}
              renderItem={renderItem}
              loadMore={loadMore}
              hasMore={hasMore}
              isLoading={isLoading}
              itemHeight={itemHeight}
              containerHeight="100%"
              style={{
                flex: 1,
                overflow: "auto",
                marginRight: "-8px",
                width: "100%",
              }}
              onScroll={handleScroll}
              newItemsText="unread transactions above"
              isItemUnread={(item) =>
                readStatus[item.id] === NOTIFICATION_STATUS.UNREAD
              }
              onPrepend={(items) => {
                console.log("prepend", items);
                //play sound
                play();
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TrackedTransactions;
